import { Download, Palette, RotateCcw, Share2, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { downloadSTLFile, downloadOpenSCADFile } from '@/utils/downloadUtils';
import { useBlob } from '@/contexts/BlobContext';
import { ColorPicker } from '@/components/parameter/ColorPicker';
import { useTranslation } from 'react-i18next';
import {
  buildParameterUpdatedContent,
  useChangeParameters,
} from '@/services/messageService';
import { specParametersToArtifactParameters } from '@/lib/specParametersToArtifactParameters';
import type { Message, Parameter } from '@shared/types';
import {
  calculateParameterRange,
  calculateParameterStep,
  translateParameterLabel,
  validateParameterValue,
} from '@/utils/parameterUtils';
import { toPlainMessage } from '@/utils/plainMessage';

const FORMATS = ['stl', 'scad', 'json'] as const;
type Format = (typeof FORMATS)[number];

export function ParameterSection() {
  const { t } = useTranslation('common');
  const { blob } = useBlob();
  const { currentMessage, setCurrentMessage } = useCurrentMessage();
  const [selectedFormat, setSelectedFormat] = useState<Format>('stl');
  const changeParameters = useChangeParameters();
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPersistRef = useRef<{
    message: Message;
    parameters: Parameter[];
  } | null>(null);

  const artifact = currentMessage?.content.artifact;
  const parameters = useMemo(() => {
    const direct = artifact?.parameters ?? [];
    if (direct.length > 0) return direct;
    return specParametersToArtifactParameters(artifact?.spec?.parameters ?? []);
  }, [artifact?.parameters, artifact?.spec?.parameters]);
  const parameterGroups = useMemo(() => groupParameters(parameters), [parameters]);
  const formatAvailable: Record<Format, boolean> = {
    stl: !!blob,
    scad: !!artifact?.code,
    json: !!artifact?.spec,
  };
  const hasAnyDownload =
    formatAvailable.stl || formatAvailable.scad || formatAvailable.json;
  const isDownloadDisabled = !formatAvailable[selectedFormat];

  const handleDownload = () => {
    if (selectedFormat === 'stl') {
      if (!blob) return;
      downloadSTLFile(blob, currentMessage);
      return;
    }
    if (selectedFormat === 'scad') {
      if (!artifact?.code) return;
      downloadOpenSCADFile(artifact.code, currentMessage);
      return;
    }
    const spec = artifact?.spec;
    if (!spec) return;
    const titleSeed =
      (artifact?.title || spec.title || 'spec')
        .replace(/[^a-z0-9_-]+/gi, '_')
        .slice(0, 60) || 'spec';
    const blobOut = new Blob([JSON.stringify(spec, null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blobOut);
    a.download = `${titleSeed}.spec.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const flushPersist = useCallback(() => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    const pending = pendingPersistRef.current;
    if (!pending) return;
    pendingPersistRef.current = null;
    changeParameters(pending.message, pending.parameters);
  }, [changeParameters]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      const pending = pendingPersistRef.current;
      if (pending) {
        pendingPersistRef.current = null;
        changeParameters(pending.message, pending.parameters);
      }
    };
  }, [changeParameters]);

  const handleParameterChange = useCallback(
    (
      param: Parameter,
      rawValue: Parameter['value'],
      mode: 'preview' | 'commit',
    ) => {
      if (!currentMessage) return;
      const baseMessage = toPlainMessage(currentMessage);
      const nextValue = validateParameterValue(param, rawValue);
      const nextParam = { ...param, value: nextValue };
      const nextParameters = parameters.some((p) => p.name === param.name)
        ? parameters.map((p) => (p.name === param.name ? nextParam : p))
        : [...parameters, nextParam];

      const nextMessage: Message = {
        ...baseMessage,
        content: buildParameterUpdatedContent(
          baseMessage,
          nextParameters,
          { dropFiles: false, updateCode: mode === 'commit' },
        ),
      };

      setCurrentMessage(nextMessage);

      pendingPersistRef.current = {
        message: nextMessage,
        parameters: nextParameters,
      };
      if (mode === 'commit') {
        flushPersist();
      }
    },
    [
      currentMessage,
      flushPersist,
      parameters,
      setCurrentMessage,
    ],
  );

  return (
    <div className="flex h-full w-full flex-col bg-synergy-bg-dark">
      {/* Header — left padding clears the absolute collapse toggle */}
      <div className="flex h-10 flex-shrink-0 items-center gap-2 pl-10 pr-4">
        <span className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-synergy-neutral-200">
          {t('output.title')}
        </span>
      </div>
      <div className="mx-4 h-px flex-shrink-0 bg-synergy-neutral-700/70" />

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-4 pt-5">
        {/* Editable parameters */}
        <section className="flex flex-col gap-2.5">
          <SectionLabel
            icon={<SlidersHorizontal className="h-3 w-3" strokeWidth={2.25} />}
            label={t('parameter.controls')}
          />
          {parameters.length > 0 ? (
            <div className="flex flex-col gap-3">
              {parameterGroups.map(([group, params]) => (
                <div key={group} className="flex flex-col gap-2">
                  {group !== 'default' && (
                    <div className="px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-synergy-neutral-500">
                      {translateParameterLabel(group)}
                    </div>
                  )}
                  {params.map((param) => (
                    <ParameterControl
                      key={param.name}
                      param={param}
                      onChange={handleParameterChange}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-synergy-neutral-500/60 px-3 py-4 text-center">
              <p className="text-[11px] leading-relaxed text-synergy-neutral-400">
                {t('parameter.noEditable')}
              </p>
            </div>
          )}
        </section>

        {/* Appearance */}
        <section className="flex flex-col gap-2.5">
          <SectionLabel
            icon={<Palette className="h-3 w-3" strokeWidth={2.25} />}
            label={t('output.appearance')}
          />
          <ColorPicker />
        </section>

        {/* Export */}
        <section className="flex flex-col gap-2.5">
          <SectionLabel
            icon={<Share2 className="h-3 w-3" strokeWidth={2.25} />}
            label={t('output.export')}
          />

          {hasAnyDownload ? (
            <div className="flex flex-col gap-2.5">
              {/* Format pills — three equal segments */}
              <div
                className="flex h-9 overflow-hidden rounded-lg bg-synergy-neutral-800"
                role="tablist"
                aria-label={t('output.formatGroupAria')}
              >
                {FORMATS.map((fmt) => {
                  const active = selectedFormat === fmt;
                  return (
                    <button
                      key={fmt}
                      role="tab"
                      aria-selected={active}
                      onClick={() => setSelectedFormat(fmt)}
                      disabled={!formatAvailable[fmt]}
                      className={`flex flex-1 items-center justify-center text-[11px] font-semibold uppercase tracking-wider transition-all duration-150 ease-out disabled:opacity-30 ${
                        active
                          ? 'bg-synergy-neutral-700 text-synergy-text-primary shadow-[inset_0_-1px_0_rgba(23,12,121,0.18)]'
                          : 'text-synergy-neutral-400 [@media(hover:hover)]:hover:text-synergy-neutral-200'
                      }`}
                    >
                      .{fmt}
                    </button>
                  );
                })}
              </div>

              {/* Primary CTA */}
              <Button
                onClick={handleDownload}
                disabled={isDownloadDisabled}
                aria-label={t('parameter.downloadAria', {
                  format: selectedFormat.toUpperCase(),
                })}
                className="h-10 w-full rounded-lg bg-synergy-neutral-50 text-xs font-semibold tracking-wide text-white transition-colors duration-150 ease-out hover:bg-synergy-neutral-50/90 disabled:bg-synergy-neutral-700 disabled:text-synergy-neutral-300"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {t('parameter.download')}
              </Button>

              <p className="text-[10px] leading-relaxed text-synergy-neutral-400">
                {t('output.exportHint', {
                  format: selectedFormat.toUpperCase(),
                })}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-synergy-neutral-500/60 px-3 py-4 text-center">
              <p className="text-[11px] leading-relaxed text-synergy-neutral-400">
                {t('output.exportEmpty')}
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function groupParameters(parameters: Parameter[]): Array<[string, Parameter[]]> {
  const groups = new Map<string, Parameter[]>();
  for (const param of parameters) {
    const key = param.group || 'default';
    groups.set(key, [...(groups.get(key) ?? []), param]);
  }
  return Array.from(groups.entries());
}

function ParameterControl({
  param,
  onChange,
}: {
  param: Parameter;
  onChange: (
    param: Parameter,
    value: Parameter['value'],
    mode: 'preview' | 'commit',
  ) => void;
}) {
  if (param.type === 'boolean' || typeof param.value === 'boolean') {
    return <BooleanParameterControl param={param} onChange={onChange} />;
  }
  if (param.type === 'string' || typeof param.value === 'string') {
    return <StringParameterControl param={param} onChange={onChange} />;
  }
  if (typeof param.value === 'number' || param.type === 'number' || !param.type) {
    return <NumberParameterControl param={param} onChange={onChange} />;
  }
  return <ReadonlyParameterControl param={param} />;
}

function NumberParameterControl({
  param,
  onChange,
}: {
  param: Parameter;
  onChange: (
    param: Parameter,
    value: Parameter['value'],
    mode: 'preview' | 'commit',
  ) => void;
}) {
  const { t } = useTranslation('common');
  const value = Number(param.value);
  const safeValue = Number.isFinite(value) ? value : Number(param.defaultValue) || 0;
  const [draft, setDraft] = useState(safeValue);
  const { min, max } = calculateParameterRange(param);
  const step = calculateParameterStep(param);

  useEffect(() => {
    setDraft(safeValue);
  }, [safeValue]);

  const commit = useCallback(
    (next: number) => {
      const clamped = Math.min(max, Math.max(min, next));
      setDraft(clamped);
      onChange(param, clamped, 'commit');
    },
    [max, min, onChange, param],
  );

  const preview = useCallback(
    (next: number) => {
      const clamped = Math.min(max, Math.max(min, next));
      setDraft(clamped);
      onChange(param, clamped, 'preview');
    },
    [max, min, onChange, param],
  );

  return (
    <div className="rounded-lg bg-synergy-neutral-800 px-3 py-3">
      <div className="mb-2 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-semibold text-synergy-text-primary">
            {translateParameterLabel(param.displayName || param.name)}
          </div>
          {param.description && (
            <div className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-synergy-neutral-400">
              {translateParameterLabel(param.description)}
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label={t('parameter.reset')}
          onClick={() => commit(Number(param.defaultValue) || 0)}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-synergy-neutral-400 transition-colors hover:bg-synergy-neutral-700 hover:text-synergy-text-primary"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <Slider
          value={[draft]}
          min={min}
          max={max}
          step={step}
          defaultValue={[Number(param.defaultValue) || 0]}
          onValueChange={([next]) => preview(next ?? draft)}
          onValueCommit={([next]) => commit(next ?? draft)}
          className="flex-1"
        />
        <input
          type="number"
          value={formatNumberInput(draft)}
          step={step}
          min={min}
          max={max}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) preview(next);
          }}
          onBlur={() => commit(draft)}
          className="h-8 w-20 rounded-md bg-synergy-neutral-900 px-2 text-right font-mono text-[11px] text-synergy-text-primary outline-none focus:ring-1 focus:ring-brand-teal"
        />
      </div>
    </div>
  );
}

function BooleanParameterControl({
  param,
  onChange,
}: {
  param: Parameter;
  onChange: (
    param: Parameter,
    value: Parameter['value'],
    mode: 'preview' | 'commit',
  ) => void;
}) {
  const checked = Boolean(param.value);
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg bg-synergy-neutral-800 px-3 py-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(param, event.target.checked, 'commit')}
        className="h-4 w-4 rounded border-synergy-neutral-500 accent-brand-teal"
      />
      <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-synergy-text-primary">
        {translateParameterLabel(param.displayName || param.name)}
      </span>
    </label>
  );
}

function StringParameterControl({
  param,
  onChange,
}: {
  param: Parameter;
  onChange: (
    param: Parameter,
    value: Parameter['value'],
    mode: 'preview' | 'commit',
  ) => void;
}) {
  const [draft, setDraft] = useState(String(param.value ?? ''));
  useEffect(() => {
    setDraft(String(param.value ?? ''));
  }, [param.value]);

  return (
    <div className="rounded-lg bg-synergy-neutral-800 px-3 py-3">
      <label className="mb-2 block truncate text-[12px] font-semibold text-synergy-text-primary">
        {translateParameterLabel(param.displayName || param.name)}
      </label>
      <input
        type="text"
        value={draft}
        maxLength={param.maxLength}
        onChange={(event) => {
          setDraft(event.target.value);
          onChange(param, event.target.value, 'preview');
        }}
        onBlur={() => onChange(param, draft, 'commit')}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
        className="h-8 w-full rounded-md bg-synergy-neutral-900 px-2 text-[12px] text-synergy-text-primary outline-none focus:ring-1 focus:ring-brand-teal"
      />
    </div>
  );
}

function ReadonlyParameterControl({ param }: { param: Parameter }) {
  return (
    <div className="rounded-lg bg-synergy-neutral-800 px-3 py-3">
      <div className="truncate text-[12px] font-semibold text-synergy-text-primary">
        {translateParameterLabel(param.displayName || param.name)}
      </div>
      <div className="mt-1 truncate font-mono text-[11px] text-synergy-neutral-400">
        {JSON.stringify(param.value)}
      </div>
    </div>
  );
}

function formatNumberInput(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Number(value.toFixed(4));
  return String(rounded);
}

function SectionLabel({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-synergy-neutral-300">
      {icon}
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
        {label}
      </span>
    </div>
  );
}
